import React from 'react';
import { Button, Pagination } from 'carbon-components-react';
import { Download16, ChevronLeft16, ChevronRight16 } from '@carbon/icons-react';
import { ReportActionsProps } from '../../types';

/**
 * ReportActions component for export controls and pagination.
 *
 * WHAT: Provides export buttons (CSV, XLSX, PDF) and pagination controls.
 *
 * WHY: Users need to be able to download report data in various formats
 * for offline use, sharing, or integration with other tools.
 *
 * HOW: When paginationOnly is true, only renders Carbon Pagination component
 * matching the old reports module style.
 */
export const ReportActions: React.FC<ReportActionsProps> = ({
  pagination,
  currentPage = 1,
  totalPages,
  onPageChange,
  onExportCsv,
  onExportXlsx,
  onExportPdf,
  loading,
  disabled,
  paginationOnly = false
}) => {
  const isDisabled = disabled || loading;

  // Handle page change from Carbon Pagination
  const handlePaginationChange = (e: { page: number }) => {
    if (onPageChange) {
      onPageChange(e.page);
    }
  };

  // Handle previous page (for simple pagination)
  const handlePrevious = () => {
    if (onPageChange && currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  // Handle next page (for simple pagination)
  const handleNext = () => {
    if (onPageChange) {
      onPageChange(currentPage + 1);
    }
  };

  // Pagination-only mode - used in the action tools bar
  if (paginationOnly) {
    const effectiveTotalPages = totalPages || pagination?.totalPages;

    return (
      <Pagination
        onChange={handlePaginationChange}
        backwardText="Previous page"
        forwardText="Next page"
        page={currentPage}
        totalItems={effectiveTotalPages}
        pageNumberText="Page Number"
        pageSizes={[1]}
        pageRangeText={() =>
          `of ${effectiveTotalPages || 1} ${
            effectiveTotalPages === 1 ? 'page' : 'pages'
          }`
        }
      />
    );
  }

  // Full report actions with export buttons and pagination
  return (
    <div className="report-actions">
      <div
        className="report-actions__controls"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}
      >
        {/* Export buttons */}
        <div
          className="report-actions__export"
          style={{ display: 'flex', gap: '0.5rem' }}
        >
          <Button
            kind="tertiary"
            size="small"
            renderIcon={Download16}
            onClick={onExportCsv}
            disabled={isDisabled}
          >
            CSV
          </Button>
          <Button
            kind="tertiary"
            size="small"
            renderIcon={Download16}
            onClick={onExportXlsx}
            disabled={isDisabled}
          >
            Excel
          </Button>
          <Button
            kind="tertiary"
            size="small"
            renderIcon={Download16}
            onClick={onExportPdf}
            disabled={isDisabled}
          >
            PDF
          </Button>
        </div>

        {/* Simple pagination controls */}
        {onPageChange && (
          <div
            className="report-actions__pagination"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Button
              kind="ghost"
              size="small"
              hasIconOnly
              renderIcon={ChevronLeft16}
              iconDescription="Previous page"
              onClick={handlePrevious}
              disabled={isDisabled || currentPage <= 1}
            />
            <span style={{ minWidth: '80px', textAlign: 'center' }}>
              Page {currentPage}
            </span>
            <Button
              kind="ghost"
              size="small"
              hasIconOnly
              renderIcon={ChevronRight16}
              iconDescription="Next page"
              onClick={handleNext}
              disabled={isDisabled}
            />
          </div>
        )}

        {/* Full pagination info when available */}
        {pagination && (
          <div
            className="report-actions__info"
            style={{ fontSize: '0.875rem' }}
          >
            <span>
              Showing {(pagination.currentPage - 1) * pagination.pageSize + 1}-
              {Math.min(
                pagination.currentPage * pagination.pageSize,
                pagination.totalRecords
              )}{' '}
              of {pagination.totalRecords} records
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ReportActions);
